// src/components/Bracket/SingleElimBracketView.jsx
//
// Data-driven single-elimination view, styled to match ThreeGGBracketView.
// - Reuses the gg3-* CSS so cards, connectors, zoom and header look identical
// - Round labels derived from totalRounds (Round of 16 / Quarter-Finals / ...)
// - Empty slots show "Winner of Game N", reverse-derived from
//   nextGameId + nextGamePosition (same trick as the 3GG view)
// - Field and time are NEVER auto-filled: they show "—" until an admin sets
//   them with the ✎ button on each card
// - Connectors derived from nextGameId, so byes / odd bracket sizes just work

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import logo from "../../assets/images/usmlsa_new.png";
import "./ThreeGGBracketView.css";
import "./SingleElimBracketView.css";

// Shared empty array — a fresh [] literal as a default would be a new
// reference on every render, which cascades through the useMemos below and
// re-fires the connector effect forever.
const EMPTY = [];

export default function SingleElimBracketView({
  games = EMPTY,
  isAdmin,
  onEnterScore,
  onEditGame,
  bracket,
  onPublish,
  onRegenerate,
  onDelete,
  publishing,
}) {
  const [zoom, setZoom] = useState(0.8);

  const isDraft = bracket?.bracketStatus === "DRAFT";
  const showSeeds = bracket?.showSeeds !== false; // default true unless explicitly false

  // ---- group into rounds --------------------------------------------------
  const rounds = useMemo(() => {
    const byRound = new Map();
    for (const g of games) {
      const r = g.roundNumber ?? 0;
      if (!byRound.has(r)) byRound.set(r, []);
      byRound.get(r).push(g);
    }
    for (const arr of byRound.values()) {
      arr.sort((a, b) => (a.gamePosition ?? 0) - (b.gamePosition ?? 0));
    }
    return [...byRound.entries()].sort((a, b) => a[0] - b[0]);
  }, [games]);

  const totalRounds = bracket?.totalRounds || (rounds.length ? rounds[rounds.length - 1][0] : 0);

  // ---- display numbers: Game 1..N, round-major ----------------------------
  // Used for the "Winner of Game N" placeholders and the card footer.
  const gameNumbers = useMemo(() => {
    const map = new Map();
    let n = 1;
    for (const [, gs] of rounds) {
      for (const g of gs) map.set(g.bracketGameId, n++);
    }
    return map;
  }, [rounds]);

  // ---- feeders: which game fills HOME / AWAY of each game -----------------
  const feeders = useMemo(() => {
    const map = new Map(); // nextGameId -> { HOME: game, AWAY: game }
    for (const g of games) {
      if (!g.nextGameId) continue;
      const slot = (g.nextGamePosition || "").toUpperCase() === "AWAY" ? "AWAY" : "HOME";
      const entry = map.get(g.nextGameId) || {};
      entry[slot] = g;
      map.set(g.nextGameId, entry);
    }
    return map;
  }, [games]);

  const placeholderFor = useCallback(
    (game, slot) => {
      // A schedule-first bracket knows the seed before it knows the team, so
      // an empty slot with a seed reads "Seed 4" rather than a bare TBD.
      const seed = slot === "AWAY" ? game.awayTeamSeed : game.homeTeamSeed;
      if (seed != null) return `Seed ${seed}`;

      const feeder = feeders.get(game.bracketGameId)?.[slot];
      if (!feeder) return "TBD";
      const n = gameNumbers.get(feeder.bracketGameId);
      return n ? `Winner of Game ${n}` : "TBD";
    },
    [feeders, gameNumbers]
  );

  const roundsForRender = useMemo(
    () =>
      rounds.map(([round, gs]) => ({
        round,
        games: gs,
        label: roundLabel(round, totalRounds),
      })),
    [rounds, totalRounds]
  );

  // ---- connectors ---------------------------------------------------------
  const connections = useMemo(() => {
    const ids = new Set(games.map((g) => g.bracketGameId));
    const conns = [];
    for (const g of games) {
      if (g.nextGameId && ids.has(g.nextGameId)) {
        conns.push({ from: g.bracketGameId, to: g.nextGameId });
      }
    }
    return conns;
  }, [games]);

  return (
    <div className="gg3-container se-view">
      {/* Header: title + actions + zoom — same shape as the 3GG header */}
      <div className="gg3-header">
        <div className="gg3-header-left">
          <div className="gg3-title-row">
            <img
              src={logo}
              alt="USMLSA"
              className="gg3-logo"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <h1 className="gg3-title">{bracket?.bracketName || "Bracket"}</h1>
          </div>
          <div className="gg3-subtitle">
            {bracket?.tournamentName} — {bracket?.divisionName} · Single Elimination
          </div>
        </div>

        <div className="gg3-header-right">
          {isAdmin && (
            <div className="gg3-actions">
              {isDraft && onPublish && (
                <button
                  className="gg3-btn gg3-btn-primary"
                  onClick={onPublish}
                  disabled={publishing}
                >
                  {publishing ? "Publishing…" : "↗ Publish Bracket"}
                </button>
              )}
              {onRegenerate && (
                <button className="gg3-btn gg3-btn-ghost" onClick={onRegenerate}>
                  🔄 Regenerate from Standings
                </button>
              )}
              {onDelete && (
                <button className="gg3-btn gg3-btn-danger" onClick={onDelete}>
                  🗑 Delete Bracket
                </button>
              )}
            </div>
          )}
          <div className="gg3-controls">
            <button onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(2)))}>+</button>
            <button onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}>−</button>
            <span className="gg3-zoom">{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(0.8)}>Reset</button>
          </div>
        </div>
      </div>

      <div className="gg3-section-title">
        Bracket ({games.length} game{games.length !== 1 ? "s" : ""})
      </div>

      <BracketViewport
        rounds={roundsForRender}
        connections={connections}
        zoom={zoom}
        color="#0066cc"
        showSeeds={showSeeds}
        isAdmin={isAdmin}
        scoringLocked={isDraft}
        onEnterScore={onEnterScore}
        onEditGame={onEditGame}
        gameNumbers={gameNumbers}
        placeholderFor={placeholderFor}
      />
    </div>
  );
}

// ===========================================================================
function BracketViewport({
  rounds,
  connections,
  zoom,
  color,
  showSeeds,
  isAdmin,
  scoringLocked,
  onEnterScore,
  onEditGame,
  gameNumbers,
  placeholderFor,
}) {
  const innerRef = useRef(null);
  const svgRef = useRef(null);
  const viewportRef = useRef(null);

  useEffect(() => {
    const run = () => {
      const inner = innerRef.current;
      const viewport = viewportRef.current;
      if (!inner || !viewport) return;

      // The pane can be display:none when this first fires — an inactive tab,
      // a collapsed panel. Everything measures 0 then, which would size the
      // viewport to nothing and leave the bracket invisible until something
      // re-triggers the effect. Bail and let the observer below call us back
      // once it actually has a size.
      if (inner.offsetWidth === 0 || inner.offsetHeight === 0) return;

      layoutColumns(inner, connections);
      drawConnectors(inner, svgRef.current, connections, zoom, color);

      // CSS transform: scale() paints smaller/larger but reserves the
      // UNSCALED box, so the viewport ends up padded with white at low zoom
      // and clipping the bracket at high zoom. Size it from the real content
      // height instead.
      const target = `${inner.offsetHeight * zoom}px`;
      if (viewport.style.height !== target) {
        viewport.style.height = target;
        viewport.style.minHeight = "0px";
      }
    };

    const t1 = setTimeout(run, 60);
    const t2 = setTimeout(run, 260);

    // Fires when the pane goes from hidden to visible, which is the case the
    // timers above can't catch.
    let observer;
    if (typeof ResizeObserver !== "undefined" && innerRef.current) {
      observer = new ResizeObserver(() => run());
      observer.observe(innerRef.current);
    }

    const onResize = () => run();
    window.addEventListener("resize", onResize);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [connections, zoom, color, rounds]);

  return (
    <div className="gg3-viewport" ref={viewportRef}>
      <div className="gg3-scaler" style={{ transform: `scale(${zoom})` }}>
        <div className="gg3-inner" ref={innerRef}>
          <div className="gg3-header-row">
            {rounds.map(({ round, label }) => (
              <div className="gg3-col-header" key={round}>
                {label}
              </div>
            ))}
          </div>

          <svg className="gg3-connectors" ref={svgRef} />

          <div className="gg3-bracket">
            {rounds.map(({ round, games }) => (
              <div className="gg3-round" key={round}>
                {games.map((g) => (
                  <GameCard
                    key={g.bracketGameId}
                    game={g}
                    gameNumber={gameNumbers.get(g.bracketGameId)}
                    placeholderFor={placeholderFor}
                    showSeeds={showSeeds}
                    isAdmin={isAdmin}
                    scoringLocked={scoringLocked}
                    onEnterScore={onEnterScore}
                    onEditGame={onEditGame}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
function GameCard({
  game,
  gameNumber,
  placeholderFor,
  showSeeds,
  isAdmin,
  scoringLocked,
  onEnterScore,
  onEditGame,
}) {
  const isCompleted = game.gameStatus === "COMPLETED";
  const homeWinner = isCompleted && game.homeTeamScore > game.awayTeamScore;
  const awayWinner = isCompleted && game.awayTeamScore > game.homeTeamScore;

  const homeReal = isRealTeam(game.homeTeamName);
  const awayReal = isRealTeam(game.awayTeamName);
  const bothTeamsPresent = homeReal && awayReal;

  // Scores stay locked until the bracket is published — a draft can still be
  // regenerated, which would wipe any results entered against it.
  const canScore = isAdmin && bothTeamsPresent && !scoringLocked;
  const canEdit = isAdmin; // field/time editable on any card, even empty ones

  return (
    <div
      className={`gg3-game ${canScore ? "clickable" : ""} ${isCompleted ? "scored" : ""}`}
      data-game-id={game.bracketGameId}
      title={
        scoringLocked && bothTeamsPresent
          ? "Publish the bracket before entering scores"
          : undefined
      }
      onClick={() => canScore && onEnterScore && onEnterScore(game)}
    >
      <TeamRow
        showSeeds={showSeeds}
        seed={game.homeTeamSeed}
        real={homeReal}
        name={homeReal ? game.homeTeamName : placeholderFor(game, "HOME")}
        winner={homeWinner}
        isCompleted={isCompleted}
        score={game.homeTeamScore}
        rightMeta={!isCompleted ? game.fieldNumber || "—" : null}
      />
      <TeamRow
        showSeeds={showSeeds}
        seed={game.awayTeamSeed}
        real={awayReal}
        name={awayReal ? game.awayTeamName : placeholderFor(game, "AWAY")}
        winner={awayWinner}
        isCompleted={isCompleted}
        score={game.awayTeamScore}
        rightMeta={!isCompleted ? formatTime(game.gameDateTime) : null}
      />

      <div className="gg3-footer">
        {gameNumber != null && <span className="gg3-slot-id">Game {gameNumber}</span>}
        {canEdit && (
          <button
            className="gg3-edit"
            title="Set field / time"
            onClick={(e) => {
              e.stopPropagation();
              onEditGame && onEditGame(game);
            }}
          >
            ✎
          </button>
        )}
      </div>
    </div>
  );
}

function TeamRow({ showSeeds, seed, real, name, winner, isCompleted, score, rightMeta }) {
  return (
    <div className={`gg3-row ${winner ? "winner" : ""}`}>
      {showSeeds && <span className="gg3-seed">{seed != null ? seed : ""}</span>}
      <span className={`gg3-name ${real ? "" : "gg3-placeholder"}`}>{name}</span>
      <span className="gg3-right">
        {isCompleted ? (
          <>
            {winner && <span className="gg3-check">✓</span>}
            <span className={`gg3-score ${winner ? "win" : ""}`}>{score != null ? score : "-"}</span>
          </>
        ) : (
          <span className="gg3-meta">{rightMeta}</span>
        )}
      </span>
    </div>
  );
}

// ---- helpers --------------------------------------------------------------
function isRealTeam(name) {
  return !!name && name !== "TBD" && String(name).trim() !== "";
}

function roundLabel(round, totalRounds) {
  if (!totalRounds) return `Round ${round}`;
  const fromEnd = totalRounds - round; // 0 = final
  if (fromEnd === 0) return "Final";
  if (fromEnd === 1) return "Semi-Finals";
  if (fromEnd === 2) return "Quarter-Finals";
  return `Round of ${Math.pow(2, fromEnd + 1)}`;
}

function formatTime(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-US", { hour: "numeric", minute: "2-digit" });
}

/**
 * Vertical placement, one column at a time, left to right.
 *
 * A game sits centred on the games that feed it — so a game waiting on a
 * single feeder (its other slot came from a bye) lines up with that feeder
 * instead of drifting to the middle of the column. Flex centring can't do
 * this because it knows nothing about which card feeds which.
 *
 * Applied as margins rather than absolute positioning so the cards stay in
 * flow and keep their column width.
 */
function layoutColumns(inner, connections) {
  if (!inner) return;

  const GAP = 24; // minimum vertical gap between cards in a column

  const feedersByTarget = new Map();
  for (const c of connections) {
    const key = String(c.to);
    if (!feedersByTarget.has(key)) feedersByTarget.set(key, []);
    feedersByTarget.get(key).push(String(c.from));
  }

  // gameId -> vertical centre, filled in as we go so the next column can
  // read the column before it.
  const centreById = new Map();

  const columns = [...inner.querySelectorAll(".gg3-round")];

  for (const col of columns) {
    const cards = [...col.querySelectorAll(".gg3-game")];
    if (cards.length === 0) continue;

    const H = cards[0].offsetHeight || 76;

    // Preferred top for each card: centred on its feeders when we know them,
    // evenly spaced otherwise (which is what the first column always gets).
    const tops = cards.map((card, i) => {
      const id = card.getAttribute("data-game-id");
      const centres = (feedersByTarget.get(id) || [])
        .map((f) => centreById.get(f))
        .filter((v) => v != null);

      if (centres.length > 0) {
        const avg = centres.reduce((a, b) => a + b, 0) / centres.length;
        return avg - H / 2;
      }
      return i * (H + GAP);
    });

    // Keep the column in order and never let two cards overlap.
    for (let i = 0; i < tops.length; i++) {
      if (i > 0) tops[i] = Math.max(tops[i], tops[i - 1] + H + GAP);
      if (tops[i] < 0) tops[i] = 0;
    }

    let prevBottom = 0;
    cards.forEach((card, i) => {
      card.style.marginTop = `${tops[i] - prevBottom}px`;
      card.style.marginBottom = "0px";
      prevBottom = tops[i] + H;
      centreById.set(card.getAttribute("data-game-id"), tops[i] + H / 2);
    });
  }
}

function drawConnectors(inner, svg, connections, zoom, color) {
  if (!inner || !svg) return;
  svg.innerHTML = "";
  svg.setAttribute("width", inner.offsetWidth);
  svg.setAttribute("height", inner.offsetHeight);
  const innerRect = inner.getBoundingClientRect();

  // Geometry of a card in unscaled coordinates.
  const box = (el) => {
    const r = el.getBoundingClientRect();
    return {
      left: (r.left - innerRect.left) / zoom,
      right: (r.left - innerRect.left) / zoom + r.width / zoom,
      mid: (r.top - innerRect.top) / zoom + r.height / zoom / 2,
    };
  };

  const draw = (d) => {
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", d);
    path.setAttribute("stroke", color);
    path.setAttribute("stroke-width", "1.5");
    path.setAttribute("fill", "none");
    path.setAttribute("stroke-linecap", "square");
    path.setAttribute("stroke-linejoin", "miter");
    svg.appendChild(path);
  };

  // Group feeders by the game they advance into, so a pair renders as ONE
  // bracket — two stubs, a shared vertical, one stub into the next game —
  // instead of two elbows drawn independently on top of each other.
  const byTarget = new Map();
  for (const conn of connections) {
    if (!byTarget.has(conn.to)) byTarget.set(conn.to, []);
    byTarget.get(conn.to).push(conn.from);
  }

  for (const [targetId, fromIds] of byTarget) {
    const toEl = inner.querySelector(`[data-game-id="${targetId}"]`);
    if (!toEl) continue;
    const to = box(toEl);

    const feeders = fromIds
      .map((id) => inner.querySelector(`[data-game-id="${id}"]`))
      .filter(Boolean)
      .map(box)
      .sort((a, b) => a.mid - b.mid);

    if (feeders.length === 0) continue;

    // The vertical sits midway between the feeder column and the target.
    const feederRight = Math.max(...feeders.map((f) => f.right));
    const midX = feederRight + (to.left - feederRight) / 2;

    if (feeders.length === 1) {
      // Lone feeder (a bye left its sibling empty): single elbow.
      const f = feeders[0];
      draw(`M ${f.right} ${f.mid} L ${midX} ${f.mid} L ${midX} ${to.mid} L ${to.left} ${to.mid}`);
      continue;
    }

    const top = feeders[0];
    const bottom = feeders[feeders.length - 1];

    // Horizontal stub out of each feeder, joined by one vertical.
    draw(`M ${top.right} ${top.mid} L ${midX} ${top.mid} L ${midX} ${bottom.mid} L ${bottom.right} ${bottom.mid}`);

    // One stub from the vertical into the next game.
    const joinY = (top.mid + bottom.mid) / 2;
    if (Math.abs(joinY - to.mid) < 0.5) {
      draw(`M ${midX} ${joinY} L ${to.left} ${to.mid}`);
    } else {
      // Target isn't vertically centred between its feeders — step across.
      draw(`M ${midX} ${joinY} L ${midX} ${to.mid} L ${to.left} ${to.mid}`);
    }
  }
}